import { useState } from "react";
import jsPDF from "jspdf";
import { FiDownload, FiPrinter } from "react-icons/fi";
import logo from "../assets/logo.png";

const COMPANY_NAME = "ABM GROUP ONE";
const COMPANY_ADDRESS = "Ramnagar, Prayagraj - 212305, Uttar Pradesh, India";
const COMPANY_PHONE = "+91 8887355992";
const COMPANY_EMAIL = "support.abmgroupone@gmail.com";

const formatDate = (date) => {
  if (!date) return "-";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });
};

const loadImageAsDataUrl = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

/* ══════════════════════════════════
   WELCOME LETTER
══════════════════════════════════ */
const buildWelcomeLetterPdf = async (user) => {
  const doc = new jsPDF();
  const logoData = await loadImageAsDataUrl(logo);

  if (logoData) doc.addImage(logoData, "PNG", 15, 12, 22, 22);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(14, 19, 6);
  doc.text(COMPANY_NAME, 42, 22);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(COMPANY_ADDRESS, 42, 28);
  doc.text(`${COMPANY_PHONE}  |  ${COMPANY_EMAIL}`, 42, 33);

  doc.setDrawColor(224, 98, 7);
  doc.setLineWidth(0.8);
  doc.line(15, 40, 195, 40);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.setTextColor(14, 19, 6);
  doc.text("Welcome Letter", 105, 52, { align: "center" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  doc.setTextColor(60, 60, 60);
  doc.text(`Date: ${formatDate(new Date())}`, 15, 62);

  doc.text(`Dear ${user?.name || "Member"},`, 15, 74);

  const body = [
    `Congratulations and a warm welcome to the ${COMPANY_NAME} family! We are delighted to have you`,
    "on board as a valued member. Your membership has been successfully registered with the following",
    "details:",
  ];
  doc.text(body, 15, 84);

  const rows = [
    ["Member ID", user?.uniqueId || "-"],
    ["Full Name", user?.name || "-"],
    ["Mobile Number", user?.mobile || "-"],
    ["Email", user?.email || "-"],
    ["Sponsor ID", user?.sponsorId || "-"],
    ["Joining Date", formatDate(user?.createdAt)],
  ];

  let y = 102;
  rows.forEach(([label, value]) => {
    doc.setFont("helvetica", "bold");
    doc.text(`${label}:`, 20, y);
    doc.setFont("helvetica", "normal");
    doc.text(String(value), 70, y);
    y += 8;
  });

  y += 6;
  const closing = [
    "As a member, you now have access to your personal dashboard where you can track your business,",
    "manage your wallet, view your team, and grow your network. Our support team is always available",
    "to assist you at every step of your journey with us.",
    "",
    "We look forward to a long and successful association with you.",
  ];
  doc.text(closing, 15, y);

  y += 42;
  doc.setFont("helvetica", "bold");
  doc.text("Warm Regards,", 15, y);
  doc.text(COMPANY_NAME, 15, y + 7);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(140, 140, 140);
  doc.text(
    "This is a system-generated letter and does not require a signature.",
    105,
    285,
    { align: "center" }
  );

  return doc;
};

/* ══════════════════════════════════
   ID CARD (CR80 landscape, front + back)
══════════════════════════════════ */
const buildIdCardPdf = async (user) => {
  const doc = new jsPDF({ unit: "mm", format: [86, 54] });
  const logoData = await loadImageAsDataUrl(logo);
  const photoData = user?.profilePic ? await loadImageAsDataUrl(user.profilePic) : null;

  // ── Front side ──
  doc.setFillColor(14, 19, 6);
  doc.rect(0, 0, 86, 14, "F");
  if (logoData) doc.addImage(logoData, "PNG", 3, 2, 10, 10);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(255, 255, 255);
  doc.text(COMPANY_NAME, 16, 7);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.5);
  doc.text("Member Identity Card", 16, 11.5);

  doc.setDrawColor(224, 98, 7);
  doc.setLineWidth(1.2);
  doc.rect(4, 17, 20, 24);
  if (photoData) {
    doc.addImage(photoData, "PNG", 4.5, 17.5, 19, 23);
  } else {
    doc.setFontSize(7);
    doc.setTextColor(150, 150, 150);
    doc.text("PHOTO", 14, 29, { align: "center" });
  }

  doc.setTextColor(20, 20, 20);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text(String(user?.name || "-"), 28, 20);

  const details = [
    ["Member ID", user?.uniqueId || "-"],
    ["Mobile", user?.mobile || "-"],
    ["Sponsor ID", user?.sponsorId || "-"],
    ["Joined", formatDate(user?.createdAt)],
  ];
  let dy = 25;
  details.forEach(([label, value]) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6.5);
    doc.text(`${label}:`, 28, dy);
    doc.setFont("helvetica", "normal");
    doc.text(String(value), 46, dy);
    dy += 5;
  });

  doc.setDrawColor(220, 220, 220);
  doc.line(4, 45, 82, 45);
  doc.setFont("helvetica", "italic");
  doc.setFontSize(6);
  doc.setTextColor(120, 120, 120);
  doc.text("This card is the property of " + COMPANY_NAME + " and must be returned upon request.", 43, 50, { align: "center" });

  // ── Back side ──
  doc.addPage([86, 54], "landscape");
  doc.setFillColor(14, 19, 6);
  doc.rect(0, 0, 86, 10, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(255, 255, 255);
  doc.text("Terms & Contact", 43, 6.5, { align: "center" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.2);
  doc.setTextColor(40, 40, 40);
  const terms = [
    "1. This card is non-transferable and remains valid only while",
    "    the membership is active and in good standing.",
    "2. If found, please return to the address below or contact us.",
    "",
    COMPANY_ADDRESS,
    `Phone: ${COMPANY_PHONE}`,
    `Email: ${COMPANY_EMAIL}`,
  ];
  doc.text(terms, 5, 18);

  return doc;
};

/* ══════════════════════════════════
   SHARED PREVIEW CARD UI
══════════════════════════════════ */
const DocumentCard = ({ title, description, previewChildren, onDownload, downloading }) => (
  <div
    style={{
      background: "#fff",
      borderRadius: "16px",
      boxShadow: "0 4px 16px rgba(0,0,0,.08)",
      border: "1px solid #e2e8f0",
      padding: "20px",
      marginBottom: "20px",
    }}
  >
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px", flexWrap: "wrap", gap: "10px" }}>
      <div>
        <h3 style={{ fontSize: "17px", fontWeight: 700, color: "#0E1306", margin: 0 }}>{title}</h3>
        <p style={{ fontSize: "12.5px", color: "#64748b", marginTop: "4px" }}>{description}</p>
      </div>
      <button
        onClick={onDownload}
        disabled={downloading}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "10px 18px",
          borderRadius: "10px",
          border: "none",
          cursor: downloading ? "not-allowed" : "pointer",
          fontWeight: 600,
          fontSize: "13px",
          color: "#fff",
          background: "linear-gradient(135deg, #E06207, #A65007)",
          opacity: downloading ? 0.7 : 1,
        }}
      >
        <FiDownload size={15} />
        {downloading ? "Generating..." : "Download PDF"}
      </button>
    </div>
    <div style={{ background: "#f8fafc", borderRadius: "12px", padding: "16px", overflowX: "auto" }}>
      {previewChildren}
    </div>
  </div>
);

/* ══════════════════════════════════
   MAIN EXPORT: Welcome Letter Page
══════════════════════════════════ */
export const WelcomeLetterPage = ({ user }) => {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const doc = await buildWelcomeLetterPdf(user);
      doc.save(`Welcome-Letter-${user?.uniqueId || "member"}.pdf`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <DocumentCard
      title="Welcome Letter"
      description="Download your official membership welcome letter"
      onDownload={handleDownload}
      downloading={downloading}
      previewChildren={
        <div style={{ maxWidth: "560px", margin: "0 auto", fontFamily: "Georgia, serif", color: "#1e293b" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", borderBottom: "3px solid #E06207", paddingBottom: "10px", marginBottom: "14px" }}>
            <img src={logo} alt="logo" style={{ width: "44px", height: "44px", borderRadius: "8px" }} />
            <div>
              <div style={{ fontWeight: 800, fontSize: "18px" }}>{COMPANY_NAME}</div>
              <div style={{ fontSize: "11px", color: "#64748b" }}>{COMPANY_ADDRESS}</div>
            </div>
          </div>
          <p style={{ fontSize: "13px", marginBottom: "10px" }}>Dear {user?.name || "Member"},</p>
          <p style={{ fontSize: "13px", lineHeight: 1.6 }}>
            Congratulations and a warm welcome to the {COMPANY_NAME} family! Your membership has been
            successfully registered with the following details:
          </p>
          <table style={{ width: "100%", fontSize: "12.5px", marginTop: "10px" }}>
            <tbody>
              <tr><td style={{ padding: "4px 0", fontWeight: 700 }}>Member ID</td><td>{user?.uniqueId || "-"}</td></tr>
              <tr><td style={{ padding: "4px 0", fontWeight: 700 }}>Mobile</td><td>{user?.mobile || "-"}</td></tr>
              <tr><td style={{ padding: "4px 0", fontWeight: 700 }}>Sponsor ID</td><td>{user?.sponsorId || "-"}</td></tr>
              <tr><td style={{ padding: "4px 0", fontWeight: 700 }}>Joining Date</td><td>{formatDate(user?.createdAt)}</td></tr>
            </tbody>
          </table>
          <p style={{ fontSize: "13px", marginTop: "14px", lineHeight: 1.6 }}>
            We look forward to a long and successful association with you.
          </p>
        </div>
      }
    />
  );
};

/* ══════════════════════════════════
   MAIN EXPORT: ID Card Page
══════════════════════════════════ */
export const IdCardPage = ({ user }) => {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const doc = await buildIdCardPdf(user);
      doc.save(`ID-Card-${user?.uniqueId || "member"}.pdf`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <DocumentCard
      title="Member ID Card"
      description="Download your printable ABM Group One identity card"
      onDownload={handleDownload}
      downloading={downloading}
      previewChildren={
        <div
          style={{
            width: "320px",
            margin: "0 auto",
            borderRadius: "14px",
            overflow: "hidden",
            boxShadow: "0 6px 20px rgba(0,0,0,.15)",
            border: "1px solid #e2e8f0",
          }}
        >
          <div style={{ background: "#0E1306", padding: "10px 14px", display: "flex", alignItems: "center", gap: "8px" }}>
            <img src={logo} alt="logo" style={{ width: "30px", height: "30px", borderRadius: "6px" }} />
            <div>
              <div style={{ color: "#fff", fontWeight: 800, fontSize: "13px" }}>{COMPANY_NAME}</div>
              <div style={{ color: "#cbd5e1", fontSize: "9px" }}>Member Identity Card</div>
            </div>
          </div>
          <div style={{ background: "#fff", padding: "14px", display: "flex", gap: "12px" }}>
            <div
              style={{
                width: "70px",
                height: "84px",
                borderRadius: "6px",
                border: "2px solid #E06207",
                overflow: "hidden",
                background: "#f1f5f9",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "10px",
                color: "#94a3b8",
              }}
            >
              {user?.profilePic ? (
                <img src={user.profilePic} alt="member" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                "PHOTO"
              )}
            </div>
            <div style={{ fontSize: "11.5px", color: "#1e293b" }}>
              <div style={{ fontWeight: 800, fontSize: "13px", marginBottom: "4px" }}>{user?.name || "-"}</div>
              <div><b>ID:</b> {user?.uniqueId || "-"}</div>
              <div><b>Mobile:</b> {user?.mobile || "-"}</div>
              <div><b>Sponsor:</b> {user?.sponsorId || "-"}</div>
              <div><b>Joined:</b> {formatDate(user?.createdAt)}</div>
            </div>
          </div>
          <div style={{ background: "#f8fafc", padding: "8px 14px", fontSize: "9px", color: "#64748b", textAlign: "center", borderTop: "1px solid #e2e8f0" }}>
            Property of {COMPANY_NAME}. Non-transferable.
          </div>
        </div>
      }
    />
  );
};

export const MemberDocumentsHint = () => (
  <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#94a3b8" }}>
    <FiPrinter size={14} /> Use "Download PDF" to save or print your document.
  </div>
);
